// src/pages/customer/Careers.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { getJobs, getCareerCulture } from "../../services/firestoreService";
import Spinner from "../../components/common/Spinner";
import { FiBriefcase, FiMapPin, FiCalendar, FiArrowRight, FiAward } from "react-icons/fi";

export default function Careers() {
  const [jobs, setJobs] = useState([]);
  const [culture, setCulture] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("All Positions");

  useEffect(() => {
    Promise.all([getJobs(), getCareerCulture()]).then(([j, c]) => {
      setJobs(j);
      setCulture(c || {});
      setLoading(false);
    });
  }, []);

  const departments = useMemo(() => {
    const set = new Set(jobs.map((j) => j.department).filter(Boolean));
    return ["All Positions", ...Array.from(set)];
  }, [jobs]);

  const filteredJobs = activeTab === "All Positions"
    ? jobs
    : jobs.filter((j) => j.department === activeTab);

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Careers | Tripura Agro</title>
        <meta
          name="description"
          content="Explore current job openings at Tripura Agro Industries and join our team."
        />
      </Helmet>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* ── Page heading ── */}
        <div className="pt-10 sm:pt-14 pb-2">
          <h1 className="text-3xl sm:text-4xl font-black" style={{ color: "#1a3d1a" }}>Careers</h1>
        </div>

        {/* ── Our Culture split section ── */}
        <section className="py-8 sm:py-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-14 items-center">
            <div>
              <span
                className="inline-block text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-full mb-4"
                style={{ background: "#f0f5ea", color: "#4a6e30" }}
              >
                {culture.badge || "Our Culture"}
              </span>
              <h2 className="text-2xl sm:text-3xl font-black leading-tight mb-4" style={{ color: "#1a3d1a" }}>
                {culture.heading || "A Place Where Trust Meets Happiness"}
              </h2>
              <p className="text-sm sm:text-[15px] text-gray-600 leading-relaxed">
                {culture.paragraph ||
                  "Trust and Compassion are the two signature elements of our organizational culture, which you experience through our people practices. We are a workplace that believes in growing alongside the people that drive it, while leaving a lasting impact everywhere we go."}
              </p>
            </div>

            <div className="relative rounded-md overflow-hidden" style={{ boxShadow: "0 20px 40px -16px rgba(26,61,26,0.25)" }}>
              {culture.image ? (
                <img src={culture.image} alt={culture.heading || "Our culture"} className="w-full h-56 sm:h-72 object-cover" />
              ) : (
                <div
                  className="w-full h-56 sm:h-72 flex items-center justify-center"
                  style={{ background: "linear-gradient(135deg,#1a3d1a,#3a5c2f)" }}
                >
                  <FiAward className="w-12 h-12" style={{ color: "rgba(255,255,255,0.3)" }} />
                </div>
              )}
              <span
                className="absolute top-3 left-3 inline-flex items-center gap-1.5 text-[11px] font-bold px-3 py-1.5 rounded-full"
                style={{ background: "rgba(255,255,255,0.95)", color: "#1a3d1a" }}
              >
                <FiAward className="w-3 h-3" /> Great Place to Work
              </span>
            </div>
          </div>
        </section>

        {/* ── Tabs ── */}
        <div className="flex items-center gap-6 border-b overflow-x-auto" style={{ borderColor: "#eee" }}>
          {departments.map((dept) => (
            <button
              key={dept}
              onClick={() => setActiveTab(dept)}
              className="pb-3 text-sm font-semibold whitespace-nowrap transition-colors"
              style={{
                color: activeTab === dept ? "#1a3d1a" : "#9ca3af",
                borderBottom: activeTab === dept ? "2px solid #1a3d1a" : "2px solid transparent",
              }}
            >
              {dept}
            </button>
          ))}
        </div>

        {/* ── Listings ── */}
        <section className="py-8 sm:py-10">
          {loading ? (
            <Spinner center />
          ) : filteredJobs.length === 0 ? (
            <div className="text-center py-16 bg-gray-50 rounded-2xl border border-dashed" style={{ borderColor: "#d4d4b8" }}>
              <FiBriefcase className="w-10 h-10 mx-auto mb-3" style={{ color: "#4a6e30" }} />
              <h3 className="text-lg font-bold" style={{ color: "#2d3a1a" }}>No open positions right now</h3>
              <p className="text-sm text-gray-500 mt-1">Check back soon — new roles are posted here regularly.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {filteredJobs.map((job) => (
                <CareerCard key={job.id} job={job} />
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

function CareerCard({ job }) {
  return (
    <Link
      to={`/careers/${job.id}`}
      className="group flex flex-col bg-white rounded-2xl border p-5 transition-all duration-300 hover:-translate-y-1"
      style={{ borderColor: "#e2dcc8", boxShadow: "0 1px 3px rgba(26,61,26,0.06)" }}
      onMouseEnter={(e) => { e.currentTarget.style.boxShadow = "0 16px 32px -12px rgba(26,61,26,0.18)"; e.currentTarget.style.borderColor = "#a8d060"; }}
      onMouseLeave={(e) => { e.currentTarget.style.boxShadow = "0 1px 3px rgba(26,61,26,0.06)"; e.currentTarget.style.borderColor = "#e2dcc8"; }}
    >
      <div className="flex items-start justify-between gap-3 mb-2">
        <h3 className="font-bold text-lg leading-snug" style={{ color: "#1a3d1a" }}>
          {job.title}
        </h3>
        {job.type && (
          <span
            className="flex-shrink-0 text-[10px] font-bold uppercase tracking-wide px-2.5 py-1 rounded-full"
            style={{ background: "#f0f5ea", color: "#3a5c2f" }}
          >
            {job.type}
          </span>
        )}
      </div>

      {job.department && (
        <p className="text-sm text-gray-500 mb-2">{job.department}</p>
      )}

      {job.description && (
        <p className="text-sm text-gray-500 leading-relaxed line-clamp-2 mb-4">
          {job.description}
        </p>
      )}

      <div className="mt-auto flex flex-wrap gap-x-4 gap-y-1.5 pt-3 border-t text-xs text-gray-500" style={{ borderColor: "#eee" }}>
        {job.location && (
          <span className="flex items-center gap-1.5"><FiMapPin size={13} /> {job.location}</span>
        )}
      </div>
       <div className="mt-auto flex flex-wrap gap-x-4 gap-y-1.5 pt-3  text-xs text-gray-500">
        {job.postedAt && (
          <span className="flex items-center gap-1.5">
            <FiCalendar size={13} /> Posted: {new Date(job.postedAt).toLocaleDateString()}
          </span>
        )}
      </div>

      <div
        className="flex items-center gap-1.5 mt-4 text-sm font-bold transition-transform group-hover:translate-x-1"
        style={{ color: "#3a5c2f" }}
      >
        View Details <FiArrowRight size={14} />
      </div>
    </Link>
  );
}